// AgentContext — ONE agent chat state + WebSocket for the whole app.
// Mounted once in Layout (which persists across route changes), so an in-flight
// agent run keeps streaming when the user navigates between pages, and the
// floating widget (AgentChat) and the full-page Agent Dashboard read the SAME
// conversation instead of holding two disconnected copies.

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useAgent, type UseAgentReturn } from "../hooks/useAgent";

type AgentContextValue = UseAgentReturn & {
  draftMessage: string;
  setDraftMessage: Dispatch<SetStateAction<string>>;
  assistantVisible: boolean;
  setAssistantVisible: Dispatch<SetStateAction<boolean>>;
  assistantHasUnread: boolean;
};
const AgentContext = createContext<AgentContextValue | null>(null);

export function AgentProvider({ children }: { children: ReactNode }) {
  const value = useAgent();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const draftKey = `${value.scopeKey || ""}:${value.currentSessionId || "__new_conversation__"}`;
  const migrated = useRef<string | null>(null);
  const sourceKey = `${value.scopeKey || ""}:__new_conversation__`;
  const migrationKey = value.initialSessionId
    ? `${value.scopeKey || ""}:${value.initialSessionId}`
    : null;
  const transferring =
    migrationKey === draftKey && migrated.current !== migrationKey;
  const draftMessage =
    drafts[draftKey] ?? (transferring ? drafts[sourceKey] || "" : "");
  useEffect(() => {
    if (!migrationKey || migrated.current === migrationKey) return;
    migrated.current = migrationKey;
    setDrafts((previous) => ({
      ...previous,
      [migrationKey]: previous[migrationKey] ?? previous[sourceKey] ?? "",
      [sourceKey]: "",
    }));
  }, [migrationKey, sourceKey]);
  const setDraftMessage: Dispatch<SetStateAction<string>> = useCallback(
    (update) => {
      setDrafts((previous) => ({
        ...previous,
        [draftKey]:
          typeof update === "function"
            ? update(previous[draftKey] || "")
            : update,
      }));
    },
    [draftKey],
  );
  const [assistantVisible, setAssistantVisible] = useState(false);
  const [assistantHasUnread, setHasUnread] = useState(false);
  const seenCount = useRef(0);
  useEffect(() => {
    const fresh = value.messages.slice(seenCount.current);
    seenCount.current = value.messages.length;
    if (assistantVisible) setHasUnread(false);
    else if (fresh.some((m) => m.type === "AGENT")) setHasUnread(true);
  }, [value.messages, assistantVisible]);
  return (
    <AgentContext.Provider
      value={{
        ...value,
        draftMessage,
        setDraftMessage,
        assistantVisible,
        setAssistantVisible,
        assistantHasUnread,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
}

export function useAgentContext(): AgentContextValue {
  const ctx = useContext(AgentContext);
  if (!ctx) {
    throw new Error("useAgentContext must be used within an AgentProvider");
  }
  return ctx;
}
